import { Lock } from 'lucide-react';
import { useState } from 'react';
import { Button, Input, InputGroup } from '@/shared/ui';
import { changePasswordSchema } from '../model/schema';
import { useChangePassword } from '../model/use-change-password';
import css from './change-password-form.module.scss';

type FieldErrors = Partial<
  Record<'currentPassword' | 'newPassword' | 'confirmPassword', string>
>;

export const ChangePasswordForm = () => {
  const { changePassword, isPending } = useChangePassword();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});

  const handleSubmit = async (event: React.SyntheticEvent) => {
    event.preventDefault();
    const parsed = changePasswordSchema.safeParse({
      currentPassword,
      newPassword,
      confirmPassword,
    });

    if (!parsed.success) {
      const next: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (
          key === 'currentPassword' ||
          key === 'newPassword' ||
          key === 'confirmPassword'
        ) {
          next[key] = issue.message;
        }
      }
      setErrors(next);
      return;
    }

    setErrors({});
    const ok = await changePassword({
      currentPassword: parsed.data.currentPassword,
      newPassword: parsed.data.newPassword,
    });
    if (ok) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    }
  };

  return (
    <form className={css.root} onSubmit={handleSubmit}>
      <InputGroup
        error={errors.currentPassword}
        htmlFor="current-password"
        label="Current password"
      >
        <Input
          autoComplete="current-password"
          id="current-password"
          onChange={(e) => setCurrentPassword(e.target.value)}
          trailingIcon={<Lock size={16} />}
          type="password"
          value={currentPassword}
        />
      </InputGroup>

      <InputGroup
        error={errors.newPassword}
        htmlFor="new-password"
        label="New password"
      >
        <Input
          autoComplete="new-password"
          id="new-password"
          onChange={(e) => setNewPassword(e.target.value)}
          trailingIcon={<Lock size={16} />}
          type="password"
          value={newPassword}
        />
      </InputGroup>

      <InputGroup
        error={errors.confirmPassword}
        htmlFor="confirm-password"
        label="Confirm new password"
      >
        <Input
          autoComplete="new-password"
          id="confirm-password"
          onChange={(e) => setConfirmPassword(e.target.value)}
          trailingIcon={<Lock size={16} />}
          type="password"
          value={confirmPassword}
        />
      </InputGroup>

      <div className={css.actions}>
        <Button disabled={isPending} size="lg" type="submit" variant="dark">
          {isPending ? 'Changing...' : 'Change password'}
        </Button>
      </div>
    </form>
  );
};
